'use client';

import dynamic from 'next/dynamic';
import React from 'react';
import type { Notification, NotificationOptions } from './NotificationToasts';

// A UI dos toasts (framer-motion + Radix Toast) só é carregada quando o
// primeiro toast é disparado, mantendo essas libs fora do bundle inicial.
const NotificationToasts = dynamic(() => import('./NotificationToasts'), { ssr: false });

type Options = NotificationOptions;

const NotificationContext = React.createContext({
	success: (message: string, opt?: Options) => {},
	error: (message: string, opt?: Options) => {},
});

type Props = {
	children: React.ReactNode;
};

const NotificationProvider: React.FC<Props> = ({ children }) => {
	const [notifications, setNotifications] = React.useState<Map<string, Notification>>(new Map());
	// Uma vez que um toast tenha sido exibido, mantemos a UI montada mesmo
	// quando a lista esvazia, para que a animação de saída do último toast
	// seja executada (comportamento idêntico ao anterior).
	const [toastsMounted, setToastsMounted] = React.useState(false);

	const handleAddToast = React.useCallback((toast: Notification) => {
		setToastsMounted(true);
		setNotifications(prev => {
			const newMap = new Map(prev);
			newMap.set(String(Date.now()), { ...toast });
			return newMap;
		});
	}, []);

	const handleRemoveToast = React.useCallback((key: string) => {
		setNotifications(prev => {
			const newMap = new Map(prev);
			newMap.delete(key);
			return newMap;
		});
	}, []);

	const handleDispatchSuccess = React.useCallback(
		(message: string, opt: Options = {}) => handleAddToast({ message, type: 'success', ...opt }),
		[handleAddToast],
	);

	const handleDispatchError = React.useCallback(
		(message: string, opt: Options = {}) => handleAddToast({ message, type: 'error', ...opt }),
		[handleAddToast],
	);

	return (
		<NotificationContext.Provider
			value={React.useMemo(
				() => ({
					success: handleDispatchSuccess,
					error: handleDispatchError,
				}),
				[handleDispatchSuccess, handleDispatchError],
			)}
		>
			{toastsMounted && <NotificationToasts notifications={notifications} onRemove={handleRemoveToast} />}
			{children}
		</NotificationContext.Provider>
	);
};

export const useNotification = () => {
	const context = React.useContext(NotificationContext);
	if (context) return context;
	throw new Error('useNotification must be used within Notifications');
};

export default NotificationProvider;
